import React, { useEffect, useMemo, useState } from "react";
import AdminAPI from "../../../services/AdminAPI";
import { useACPP } from "../ACPPContext";
import {
  ModuleCard, ModuleHeader, SectionDivider, SoftPanel, InfoBanner,
  MetricCard, Grid4, BtnRow, BtnPrimary, BtnSecondary, BtnLight,
  FormInput, FormSelect, HelpText,
  EmptyState, TableWrap, Th, Td,
} from "../components/ui";

const MODULE13_KEY = "lessonPlanOutline";

function createEmptyOutline(academicYear = "") {
  return {
    rowId: "",
    academicYear,
    grade: "",
    section: "",
    subject: "",
    domain: "",
    duration: "",
    topic: "",
    unit: "",
    chapter: "",
    competencies: [],
    unitNo: "",
    unitName: "",
    chapterNo: "",
    chapterName: "",
    pedagogies: [],
    chapterScope: [],
    topicScope: [],
    suggestedMethod: "",
    note: "",
  };
}

function toSignature(value) {
  return JSON.stringify(value || []);
}

function isOutlineDraftDirty(draft) {
  if (!draft) return false;
  return !![
    draft.grade,
    draft.section,
    draft.subject,
    draft.domain,
    draft.duration,
    draft.topic,
    draft.unit,
    draft.chapter,
    ...(Array.isArray(draft.competencies) ? draft.competencies : []),
  ].some((value) => String(value || "").trim());
}

function buildOutlineLabel(row) {
  return [
    row.grade,
    row.section,
    row.domain || row.subject,
    row.topic,
  ].filter(Boolean).join(" • ");
}

export default function Module13LessonPlanOutline() {
  const {
    canEditModules1to5,
    module5OwnershipMappings,
    getM6FlatRows,
    getActiveAcademicYear,
  } = useACPP();

  const academicYear = getActiveAcademicYear();
  const [outlines, setOutlines] = useState([]);
  const [savedSnapshot, setSavedSnapshot] = useState([]);
  const [draft, setDraft] = useState(createEmptyOutline(academicYear));
  const [selectedBaseUid, setSelectedBaseUid] = useState("");
  const [domainOptions, setDomainOptions] = useState({ domains: [], subjects: [] });
  const [competencyOptions, setCompetencyOptions] = useState([]);
  const [customCompetencyInput, setCustomCompetencyInput] = useState("");
  const [customCompetencies, setCustomCompetencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState("");
  const [executionLoading, setExecutionLoading] = useState(false);
  const [executionRows, setExecutionRows] = useState([]);
  const [teacherCreatedRows, setTeacherCreatedRows] = useState([]);
  const [previewRow, setPreviewRow] = useState(null);
  const [previewCompetencyOptions, setPreviewCompetencyOptions] = useState([]);
  const [previewCompetencyLoading, setPreviewCompetencyLoading] = useState(false);
  const [autoGenerating, setAutoGenerating] = useState(false);

  const bases = useMemo(() => {
    return (module5OwnershipMappings || [])
      .filter((r) => r.grade && r.section && r.subject)
      .map((r) => ({
        uid: r.uid || `${r.grade}|${r.section}|${r.subject}`,
        label: `${r.grade} — ${r.section} — ${r.subject}`,
        grade: r.grade,
        section: r.section,
        subject: r.subject,
      }));
  }, [module5OwnershipMappings]);

  const m6Rows = useMemo(() => {
    if (typeof getM6FlatRows === "function") return getM6FlatRows() || [];
    return [];
  }, [getM6FlatRows]);

  const unitOptions = useMemo(() => {
    const seen = new Set();
    return m6Rows
      .filter((row) =>
        row.grade === draft.grade &&
        row.section === draft.section &&
        row.subject === draft.subject &&
        row.unitName &&
        row.unitName !== "—"
      )
      .map((row) => ({
        value: `${row.unitNo || ""}__${row.unitName || ""}`,
        label: [row.unitNo, row.unitName].filter((item) => item && item !== "—").join(" • ") || row.unitName,
        unitNo: row.unitNo || "",
        unitName: row.unitName || "",
      }))
      .filter((option) => {
        if (seen.has(option.value)) return false;
        seen.add(option.value);
        return true;
      });
  }, [m6Rows, draft.grade, draft.section, draft.subject]);

  const chapterOptions = useMemo(() => {
    const seen = new Set();
    const selectedUnitKey = `${draft.unitNo || ""}__${draft.unit || draft.unitName || ""}`;
    return m6Rows
      .filter((row) =>
        row.grade === draft.grade &&
        row.section === draft.section &&
        row.subject === draft.subject &&
        row.chapterName &&
        row.chapterName !== "—" &&
        (!selectedUnitKey || selectedUnitKey === "__" || `${row.unitNo || ""}__${row.unitName || ""}` === selectedUnitKey)
      )
      .map((row) => ({
        value: row.chapterName || "",
        label: row.chapterNo && row.chapterNo !== "—" ? `${row.chapterNo}. ${row.chapterName}` : row.chapterName,
        chapterNo: row.chapterNo || "",
        chapterName: row.chapterName || "",
        pedagogies: String(row.pedagogy || "")
          .split(",")
          .map((item) => item.trim())
          .filter((item) => item && item !== "—"),
      }))
      .filter((option) => {
        if (!option.value || seen.has(option.value)) return false;
        seen.add(option.value);
        return true;
      });
  }, [m6Rows, draft.grade, draft.section, draft.subject, draft.unitNo, draft.unit, draft.unitName]);

  const pedagogyOptions = useMemo(() => {
    const seen = new Set();
    return chapterOptions
      .flatMap((option) => Array.isArray(option.pedagogies) ? option.pedagogies : [])
      .filter((item) => {
        const key = String(item || "").trim().toLowerCase();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [chapterOptions]);

  const loadData = async () => {
    setLoading(true);
    setActionError("");
    try {
      const res = await AdminAPI.acppGetModuleData(MODULE13_KEY, academicYear);
      const next = Array.isArray(res?.outlines) ? res.outlines : [];
      setOutlines(next);
      setSavedSnapshot(next);
      setDraft(createEmptyOutline(academicYear));
      setSelectedBaseUid("");
    } catch (error) {
      const msg = String(error?.message || "");
      if (msg.toLowerCase().includes("404") || msg.toLowerCase().includes("not found")) {
        setOutlines([]);
        setSavedSnapshot([]);
        setDraft(createEmptyOutline(academicYear));
        setSelectedBaseUid("");
      } else {
        setActionError(msg || "Failed to load Module 13 data.");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadExecution = async () => {
    setExecutionLoading(true);
    try {
      const res = await AdminAPI.acppGetModule13Execution(academicYear);
      setExecutionRows(Array.isArray(res?.rows) ? res.rows : []);
      setTeacherCreatedRows(Array.isArray(res?.teacher_created_unmatched) ? res.teacher_created_unmatched : []);
    } catch (error) {
      setExecutionRows([]);
      setTeacherCreatedRows([]);
    } finally {
      setExecutionLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    loadExecution();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [academicYear]);

  useEffect(() => {
    if (!draft.grade) {
      setDomainOptions({ domains: [], subjects: [] });
      return;
    }
    let cancelled = false;
    AdminAPI.acppGetLessonPlanDomains(draft.grade)
      .then((res) => {
        if (cancelled) return;
        setDomainOptions({
          domains: Array.isArray(res?.domains) ? res.domains : [],
          subjects: Array.isArray(res?.subjects) ? res.subjects : [],
        });
      })
      .catch(() => {
        if (!cancelled) setDomainOptions({ domains: [], subjects: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [draft.grade]);

  // Determine if the selected domain is a custom subject (not a standard curriculum domain)
  const isCustomSubject = domainOptions.subjects.some((s) => s.name === draft.domain);

  useEffect(() => {
    setCustomCompetencies([]);
    setCustomCompetencyInput("");
    if (!draft.grade || !draft.domain || isCustomSubject) {
      setCompetencyOptions([]);
      return;
    }
    let cancelled = false;
    AdminAPI.acppGetLessonPlanCompetencies(draft.grade, draft.domain)
      .then((res) => {
        if (cancelled) return;
        setCompetencyOptions(Array.isArray(res?.competencies) ? res.competencies : []);
      })
      .catch(() => {
        if (!cancelled) setCompetencyOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [draft.grade, draft.domain, isCustomSubject]);

  const status = useMemo(() => {
    if (isOutlineDraftDirty(draft)) return "Ready to Append";
    if (!outlines.length && !savedSnapshot.length) return "Pending";
    return toSignature(outlines) === toSignature(savedSnapshot) ? "Saved" : "In Progress";
  }, [draft, outlines, savedSnapshot]);

  const applyBase = (uid) => {
    const base = bases.find((item) => item.uid === uid);
    setSelectedBaseUid(uid);
    if (!base) {
      setDraft((prev) => ({
        ...prev,
        grade: "",
        section: "",
        subject: "",
        domain: "",
        competencies: [],
      }));
      return;
    }
    setDraft((prev) => ({
      ...prev,
      academicYear,
      grade: base.grade,
      section: base.section,
      subject: base.subject,
      domain: "",
      unit: "",
      unitNo: "",
      unitName: "",
      chapter: "",
      chapterNo: "",
      chapterName: "",
      pedagogies: [],
      suggestedMethod: "",
      competencies: [],
    }));
  };

  const applyUnitSelection = (selected) => {
    setDraft((prev) => ({
      ...prev,
      unit: selected?.unitName || "",
      unitNo: selected?.unitNo || "",
      unitName: selected?.unitName || "",
      chapter: "",
      chapterNo: "",
      chapterName: "",
      pedagogies: [],
      suggestedMethod: "",
    }));
  };

  const applyChapterSelection = (selected) => {
    setDraft((prev) => ({
      ...prev,
      chapter: selected?.chapterName || "",
      chapterNo: selected?.chapterNo || "",
      chapterName: selected?.chapterName || "",
      pedagogies: Array.isArray(selected?.pedagogies) ? selected.pedagogies : [],
      suggestedMethod: Array.isArray(selected?.pedagogies) ? selected.pedagogies.join(", ") : "",
    }));
  };

  const setDraftField = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const toggleCompetency = (id) => {
    setDraft((prev) => {
      const current = Array.isArray(prev.competencies) ? prev.competencies : [];
      const exists = current.includes(id);
      return {
        ...prev,
        competencies: exists ? current.filter((item) => item !== id) : [...current, id],
      };
    });
  };

  const resetDraft = () => {
    setDraft(createEmptyOutline(academicYear));
    setSelectedBaseUid("");
    setCompetencyOptions([]);
  };

  const save = async () => {
    setSaving(true);
    setActionError("");
    try {
      if (!draft.grade || !draft.section || !draft.subject || !selectedBaseUid) {
        throw new Error("Please choose a valid Class / Section assignment.");
      }
      if (!draft.domain || !draft.duration || !draft.topic || !draft.unit || !draft.chapter) {
        throw new Error("Please complete domain, duration, topic, unit, and chapter.");
      }
      if (!Array.isArray(draft.competencies) || !draft.competencies.length) {
        throw new Error("Please select at least one competency.");
      }

      const payload = {
        outlines: [
          {
            ...draft,
            academicYear,
            unitName: draft.unit,
            chapterName: draft.chapter,
            chapterScope: draft.chapter ? [draft.chapter] : [],
            topicScope: draft.topic ? [draft.topic] : [],
            suggestedMethod: (draft.pedagogies || []).join(", "),
          },
        ],
      };

      const saved = await AdminAPI.acppSaveModuleData(MODULE13_KEY, academicYear, payload);
      const next = Array.isArray(saved?.outlines) ? saved.outlines : outlines;
      setOutlines(next);
      setSavedSnapshot(next);
      resetDraft();
      await loadExecution();
      alert("Lesson plan outline saved.");
    } catch (error) {
      setActionError(error?.message || "Failed to save Module 13.");
    } finally {
      setSaving(false);
    }
  };

  const resetYear = async () => {
    setSaving(true);
    setActionError("");
    try {
      await AdminAPI.acppDeleteModuleData(MODULE13_KEY, academicYear);
      setOutlines([]);
      setSavedSnapshot([]);
      resetDraft();
      await loadExecution();
    } catch (error) {
      setActionError(error?.message || "Failed to reset Module 13.");
    } finally {
      setSaving(false);
    }
  };

  const autoGenerate = async () => {
    if (!window.confirm(
      "Auto-generate Module 13 outlines from all approved Module 6 plans?\n\n" +
      "This will create one outline skeleton per chapter from approved plans. " +
      "Existing outlines are preserved — duplicates are skipped automatically."
    )) return;
    setAutoGenerating(true);
    setActionError("");
    try {
      const result = await AdminAPI.acppAutoGenerateModule13(academicYear);
      await loadData();
      await loadExecution();
      alert(result?.message || `Generated ${result?.generated ?? 0} outline(s).`);
    } catch (error) {
      setActionError(error?.message || "Auto-generation failed. Ensure Module 6 plans are approved.");
    } finally {
      setAutoGenerating(false);
    }
  };

  const openPreview = async (row, source) => {
    const nextRow = { ...row, source };
    setPreviewRow(nextRow);
    setPreviewCompetencyOptions([]);

    if (!nextRow.grade || !nextRow.domain) return;

    setPreviewCompetencyLoading(true);
    try {
      const res = await AdminAPI.acppGetLessonPlanCompetencies(nextRow.grade, nextRow.domain);
      setPreviewCompetencyOptions(Array.isArray(res?.competencies) ? res.competencies : []);
    } catch (_) {
      setPreviewCompetencyOptions([]);
    } finally {
      setPreviewCompetencyLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewRow(null);
    setPreviewCompetencyOptions([]);
    setPreviewCompetencyLoading(false);
  };

  return (
    <ModuleCard id="module13">
      <ModuleHeader
        step="Module 13"
        title="Lesson Plan Outline"
        description="Each save appends a new outline for teachers. Existing outlines are preserved."
        showStatus={false}
      />

      <InfoBanner>
        Choose the teacher assignment first, then complete the planning details exactly as they should appear in the lesson-plan creation flow.
      </InfoBanner>

      {!!actionError && (
        <div className="mt-3 rounded-xl border border-[#fecaca] bg-[#fff1f2] text-[#9f1239] px-3 py-2 text-sm">{actionError}</div>
      )}

      <SectionDivider />

      <Grid4>
        <MetricCard label="Saved Outlines" value={outlines.length} meta="Teacher-visible lesson-plan outline entries" />
        <MetricCard label="Planning Bases" value={bases.length} meta="Pulled from Module 5 ownership mapping" />
        <MetricCard label="Academic Year" value={academicYear || "—"} meta="Active ACPP plan year" />
        <MetricCard label="Status" value={status} meta="Append-only year-scoped save state" />
      </Grid4>

      <SectionDivider />

      {canEditModules1to5 ? (
        <SoftPanel title="Create Lesson Plan Outline">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#1D3469]">Class / Section Assignment</label>
              <FormSelect value={selectedBaseUid} onChange={(e) => applyBase(e.target.value)}>
                <option value="">Select planning base</option>
                {bases.map((base) => (
                  <option key={base.uid} value={base.uid}>{base.label}</option>
                ))}
              </FormSelect>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#1D3469]">Class / Grade</label>
              <FormInput value={draft.grade || ""} readOnly placeholder="Selected from planning base" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#1D3469]">Section</label>
              <FormInput value={draft.section || ""} readOnly placeholder="Selected from planning base" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#1D3469]">Subject Mapping</label>
              <FormInput value={draft.subject || ""} readOnly placeholder="Selected from planning base" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#1D3469]">Domain / Subject</label>
              <FormSelect value={draft.domain} onChange={(e) => setDraftField("domain", e.target.value)} disabled={!draft.grade}>
                <option value="">{draft.grade ? "Select domain or subject" : "Choose class / section first"}</option>
                {domainOptions.domains.length > 0 && (
                  <optgroup label="── Domains ──">
                    {domainOptions.domains.map((d) => (
                      <option key={d.id} value={d.name}>{d.name}</option>
                    ))}
                  </optgroup>
                )}
                {domainOptions.subjects.length > 0 && (
                  <optgroup label="── Subjects / Classes ──">
                    {domainOptions.subjects.map((s) => (
                      <option key={s.id} value={s.name}>{s.name}</option>
                    ))}
                  </optgroup>
                )}
              </FormSelect>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#1D3469]">Duration</label>
              <FormInput value={draft.duration} onChange={(e) => setDraftField("duration", e.target.value)} placeholder="e.g. 45 minutes" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#1D3469]">Topic</label>
              <FormInput value={draft.topic} onChange={(e) => setDraftField("topic", e.target.value)} placeholder="Enter lesson topic" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#1D3469]">Unit</label>
              <FormSelect
                value={`${draft.unitNo || ""}__${draft.unit || draft.unitName || ""}`}
                onChange={(e) => {
                  const selected = unitOptions.find((item) => item.value === e.target.value);
                  applyUnitSelection(selected);
                }}
                disabled={!draft.grade || !unitOptions.length}
              >
                <option value="">{draft.grade ? (unitOptions.length ? "Select unit" : "No units available") : "Choose class / section first"}</option>
                {unitOptions.map((unit) => (
                  <option key={unit.value} value={unit.value}>{unit.label}</option>
                ))}
              </FormSelect>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#1D3469]">Chapter</label>
              <FormSelect
                value={draft.chapter}
                onChange={(e) => {
                  const selected = chapterOptions.find((item) => item.value === e.target.value);
                  applyChapterSelection(selected);
                }}
                disabled={!draft.grade || !chapterOptions.length}
              >
                <option value="">{draft.grade ? (chapterOptions.length ? "Select chapter" : "No chapters available") : "Choose class / section first"}</option>
                {chapterOptions.map((chapter) => (
                  <option key={chapter.value} value={chapter.value}>{chapter.label}</option>
                ))}
              </FormSelect>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#1D3469]">Unit No.</label>
              <FormSelect
                value={draft.unitNo || ""}
                onChange={(e) => {
                  const selected = unitOptions.find((item) => item.unitNo === e.target.value);
                  applyUnitSelection(selected);
                }}
                disabled={!draft.grade || !unitOptions.length}
              >
                <option value="">{draft.grade ? (unitOptions.length ? "Select unit no." : "No unit numbers available") : "Choose class / section first"}</option>
                {unitOptions.map((unit) => (
                  <option key={`unit-no-${unit.value}`} value={unit.unitNo}>{unit.unitNo || "—"}</option>
                ))}
              </FormSelect>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#1D3469]">Unit Name</label>
              <FormSelect
                value={draft.unitName || draft.unit || ""}
                onChange={(e) => {
                  const selected = unitOptions.find((item) => item.unitName === e.target.value);
                  applyUnitSelection(selected);
                }}
                disabled={!draft.grade || !unitOptions.length}
              >
                <option value="">{draft.grade ? (unitOptions.length ? "Select unit name" : "No unit names available") : "Choose class / section first"}</option>
                {unitOptions.map((unit) => (
                  <option key={`unit-name-${unit.value}`} value={unit.unitName}>{unit.unitName || "—"}</option>
                ))}
              </FormSelect>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#1D3469]">Chapter No.</label>
              <FormSelect
                value={draft.chapterNo || ""}
                onChange={(e) => {
                  const selected = chapterOptions.find((item) => item.chapterNo === e.target.value);
                  applyChapterSelection(selected);
                }}
                disabled={!draft.grade || !chapterOptions.length}
              >
                <option value="">{draft.grade ? (chapterOptions.length ? "Select chapter no." : "No chapter numbers available") : "Choose class / section first"}</option>
                {chapterOptions.map((chapter) => (
                  <option key={`chapter-no-${chapter.value}`} value={chapter.chapterNo}>{chapter.chapterNo || "—"}</option>
                ))}
              </FormSelect>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#1D3469]">Chapter Name</label>
              <FormSelect
                value={draft.chapterName || draft.chapter || ""}
                onChange={(e) => {
                  const selected = chapterOptions.find((item) => item.chapterName === e.target.value);
                  applyChapterSelection(selected);
                }}
                disabled={!draft.grade || !chapterOptions.length}
              >
                <option value="">{draft.grade ? (chapterOptions.length ? "Select chapter name" : "No chapter names available") : "Choose class / section first"}</option>
                {chapterOptions.map((chapter) => (
                  <option key={`chapter-name-${chapter.value}`} value={chapter.chapterName}>{chapter.chapterName || "—"}</option>
                ))}
              </FormSelect>
            </div>
            <div className="space-y-2 md:col-span-2">
              <label className="text-sm font-semibold text-[#1D3469]">Pedagogies</label>
              <FormSelect
                multiple
                size={Math.min(Math.max(pedagogyOptions.length, 3), 6)}
                value={draft.pedagogies || []}
                onChange={(e) => {
                  const values = Array.from(e.target.selectedOptions || []).map((option) => option.value);
                  setDraft((prev) => ({
                    ...prev,
                    pedagogies: values,
                    suggestedMethod: values.join(", "),
                  }));
                }}
                disabled={!pedagogyOptions.length}
              >
                {pedagogyOptions.map((item) => (
                  <option key={item} value={item}>{item}</option>
                ))}
              </FormSelect>
            </div>
          </div>

          <div className="mt-6">
            <label className="text-sm font-semibold text-[#1D3469] block mb-3">Competencies</label>
            {!draft.grade || !draft.domain ? (
              <HelpText>Select class/grade and domain / subject to load competencies.</HelpText>
            ) : isCustomSubject ? (
              <div className="space-y-3">
                <p className="text-xs text-[#6b7280]">Custom competencies for subject-based lesson plans</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="flex-1 border border-[#d1d5db] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1D3469]"
                    placeholder="Type a competency and press Add"
                    value={customCompetencyInput}
                    onChange={(e) => setCustomCompetencyInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && customCompetencyInput.trim()) {
                        e.preventDefault();
                        const val = customCompetencyInput.trim();
                        if (!customCompetencies.includes(val)) {
                          setCustomCompetencies((prev) => [...prev, val]);
                          setDraft((prev) => ({ ...prev, competencies: [...(prev.competencies || []), val] }));
                        }
                        setCustomCompetencyInput("");
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="px-4 py-2 bg-[#1D3469] text-white text-sm rounded-lg hover:bg-[#162852] transition-colors"
                    onClick={() => {
                      const val = customCompetencyInput.trim();
                      if (!val || customCompetencies.includes(val)) return;
                      setCustomCompetencies((prev) => [...prev, val]);
                      setDraft((prev) => ({ ...prev, competencies: [...(prev.competencies || []), val] }));
                      setCustomCompetencyInput("");
                    }}
                  >
                    Add
                  </button>
                </div>
                {customCompetencies.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {customCompetencies.map((c, i) => (
                      <span key={i} className="flex items-center gap-1 bg-[#e8edf7] text-[#1D3469] text-xs rounded-full px-3 py-1">
                        {c}
                        <button
                          type="button"
                          className="ml-1 text-[#9ca3af] hover:text-red-500 font-bold"
                          onClick={() => {
                            setCustomCompetencies((prev) => prev.filter((_, idx) => idx !== i));
                            setDraft((prev) => ({ ...prev, competencies: (prev.competencies || []).filter((x) => x !== c) }));
                          }}
                        >×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ) : competencyOptions.length ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-72 overflow-y-auto border border-[#d9e6fb] rounded-2xl p-4 bg-[#fafcff]">
                {competencyOptions.map((item) => (
                  <label key={item.id} className="flex items-start gap-3 rounded-xl border border-[#e7eef9] bg-white px-3 py-3 text-sm">
                    <input
                      type="checkbox"
                      checked={(draft.competencies || []).includes(item.id)}
                      onChange={() => toggleCompetency(item.id)}
                      className="mt-1 h-4 w-4"
                    />
                    <span>
                      <span className="font-semibold text-[#1e5fb3]">{item.id}</span>
                      {" - "}
                      {item.text}
                      <span className="block text-xs text-[#6b7280] mt-1">Goal: {item.goalId}</span>
                    </span>
                  </label>
                ))}
              </div>
            ) : (
              <HelpText>No competencies available for the selected grade and domain.</HelpText>
            )}
          </div>

          <BtnRow>
            <BtnSecondary onClick={save} disabled={saving || loading || autoGenerating}>Save Outline</BtnSecondary>
            <BtnPrimary onClick={autoGenerate} disabled={saving || loading || autoGenerating}>
              {autoGenerating ? "Generating…" : "⚡ Auto-Generate from Module 6"}
            </BtnPrimary>
            <BtnLight onClick={resetDraft} disabled={saving || loading || autoGenerating}>Clear Form</BtnLight>
            <BtnLight onClick={async () => { await loadData(); await loadExecution(); }} disabled={saving || loading || autoGenerating}>Refresh Year Data</BtnLight>
          </BtnRow>
        </SoftPanel>
      ) : (
        <SoftPanel title="Read-only View">
          <HelpText>HOD view is read-only for this module in current rollout.</HelpText>
        </SoftPanel>
      )}


      <SoftPanel title="Outline Execution Progress">
        <p className="text-[13px] text-[#6b7280] leading-[1.7] m-0 mb-3">
          Admin-defined outlines matched against teacher lesson plan submissions. Shows completion status per outline entry.
        </p>
        {executionLoading ? (
          <EmptyState>Loading execution data…</EmptyState>
        ) : (
          <>
            <TableWrap>
              <thead>
                <tr>
                  <Th>#</Th>
                  <Th>Grade</Th>
                  <Th>Section</Th>
                  <Th>Subject</Th>
                  <Th>Domain</Th>
                  <Th>Duration</Th>
                  <Th>Topic</Th>
                  <Th>Unit</Th>
                  <Th>Chapter / Scope</Th>
                  <Th>Competencies</Th>
                  <Th>Status</Th>
                  <Th>Done Type</Th>
                  <Th>Matched Lesson</Th>
                  <Th>Action</Th>
                </tr>
              </thead>
              <tbody>
                {(executionRows || []).map((row, idx) => (
                  <tr key={row.execution_row_id || row.checklist_id || idx}>
                    <Td>{idx + 1}</Td>
                    <Td>{row.grade || "—"}</Td>
                    <Td>{row.section || "—"}</Td>
                    <Td>{row.subject || "—"}</Td>
                    <Td>{row.domain || "—"}</Td>
                    <Td>{row.duration || "—"}</Td>
                    <Td>{row.topic || "—"}</Td>
                    <Td>{`${row.unit_no || ""} ${row.unit_name || ""}`.trim() || row.unit || "—"}</Td>
                    <Td>{row.chapter || [...(row.chapter_scope || []), ...(row.topic_scope || [])].join(", ") || row.topic || "—"}</Td>
                    <Td>{Array.isArray(row.competencies) && row.competencies.length ? `${row.competencies.length} selected` : "—"}</Td>
                    <Td>{row.status === "done" ? "Done" : "Pending"}</Td>
                    <Td>{row.done_mode || "pending"}</Td>
                    <Td>{row.matched_lesson_plan_id || "—"}</Td>
                    <Td>
                      <button
                        type="button"
                        onClick={() => openPreview(row, "execution")}
                        className="rounded-lg border border-[#cfe0fb] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#1e5fb3] hover:bg-[#f6f9ff]"
                      >
                        View
                      </button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
            {!executionRows.length && <EmptyState>No execution rows yet.</EmptyState>}
          </>
        )}
      </SoftPanel>

      <SoftPanel title="Teacher-Created Lesson Plans">
        <p className="text-[13px] text-[#6b7280] leading-[1.7] m-0 mb-3">
          Lesson plans submitted by teachers that do not match any admin-defined outline for this year.
        </p>
        {executionLoading ? (
          <EmptyState>Loading…</EmptyState>
        ) : (
          <>
            <TableWrap>
              <thead>
                <tr>
                  <Th>#</Th>
                  <Th>Grade</Th>
                  <Th>Section</Th>
                  <Th>Subject</Th>
                  <Th>Domain</Th>
                  <Th>Duration</Th>
                  <Th>Topic</Th>
                  <Th>Unit</Th>
                  <Th>Chapter / Scope</Th>
                  <Th>Competencies</Th>
                  <Th>Status</Th>
                  <Th>Teacher</Th>
                  <Th>Lesson Plan ID</Th>
                  <Th>Action</Th>
                </tr>
              </thead>
              <tbody>
                {(teacherCreatedRows || []).map((row, idx) => (
                  <tr key={row.lesson_plan_id || idx}>
                    <Td>{idx + 1}</Td>
                    <Td>{row.grade || "—"}</Td>
                    <Td>{row.section || "—"}</Td>
                    <Td>{row.subject || "—"}</Td>
                    <Td>{row.domain || "—"}</Td>
                    <Td>{row.duration || "—"}</Td>
                    <Td>{row.topic || "—"}</Td>
                    <Td>{`${row.unit_no || ""} ${row.unit_name || ""}`.trim() || "—"}</Td>
                    <Td>{row.chapter || [...(row.chapter_scope || []), ...(row.topic_scope || [])].join(", ") || "—"}</Td>
                    <Td>{Array.isArray(row.competencies) && row.competencies.length ? `${row.competencies.length} selected` : "—"}</Td>
                    <Td>{row.status || "—"}</Td>
                    <Td>{row.teacher_name || "—"}</Td>
                    <Td>{row.lesson_plan_id || "—"}</Td>
                    <Td>
                      <button
                        type="button"
                        onClick={() => openPreview(row, "teacher-created")}
                        className="rounded-lg border border-[#cfe0fb] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#1e5fb3] hover:bg-[#f6f9ff]"
                      >
                        View
                      </button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
            {!teacherCreatedRows.length && <EmptyState>No unmatched teacher-created lesson plans for this year.</EmptyState>}
          </>
        )}
      </SoftPanel>

      {previewRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(15,23,42,0.45)] p-4">
          <div className="w-full max-w-3xl rounded-3xl bg-white shadow-2xl border border-[#d9e6fb] overflow-hidden">
            <div className="flex items-start justify-between gap-4 border-b border-[#e8eef8] px-6 py-5">
              <div>
                <h3 className="text-2xl font-bold text-[#1D3469]">Outline Detail Preview</h3>
                <p className="text-sm text-[#64748b] mt-1">
                  {previewRow.source === "execution" ? "Saved execution row" : "Teacher-created lesson plan"} for Module 13.
                </p>
              </div>
              <button
                type="button"
                onClick={closePreview}
                className="rounded-xl border border-[#d9e6fb] px-3 py-2 text-sm font-semibold text-[#1D3469] hover:bg-[#f8fbff]"
              >
                Close
              </button>
            </div>

            <div className="px-6 py-5 space-y-5 max-h-[75vh] overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><span className="font-semibold text-[#1D3469]">Grade:</span> {previewRow.grade || "—"}</div>
                <div><span className="font-semibold text-[#1D3469]">Section:</span> {previewRow.section || "—"}</div>
                <div><span className="font-semibold text-[#1D3469]">Subject:</span> {previewRow.subject || "—"}</div>
                <div><span className="font-semibold text-[#1D3469]">Domain:</span> {previewRow.domain || "—"}</div>
                <div><span className="font-semibold text-[#1D3469]">Duration:</span> {previewRow.duration || "—"}</div>
                <div><span className="font-semibold text-[#1D3469]">Status:</span> {previewRow.status || "—"}</div>
                <div><span className="font-semibold text-[#1D3469]">Topic:</span> {previewRow.topic || "—"}</div>
                <div><span className="font-semibold text-[#1D3469]">Unit:</span> {`${previewRow.unit_no || ""} ${previewRow.unit_name || ""}`.trim() || previewRow.unit || "—"}</div>
                <div><span className="font-semibold text-[#1D3469]">Chapter:</span> {previewRow.chapter || "—"}</div>
                <div><span className="font-semibold text-[#1D3469]">Done Type:</span> {previewRow.done_mode || "—"}</div>
                <div><span className="font-semibold text-[#1D3469]">Matched Lesson:</span> {previewRow.matched_lesson_plan_id || previewRow.lesson_plan_id || "—"}</div>
                <div><span className="font-semibold text-[#1D3469]">Teacher:</span> {previewRow.teacher_name || "—"}</div>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-[#1D3469] mb-2">Scope</h4>
                <div className="rounded-2xl border border-[#e7eef9] bg-[#fafcff] px-4 py-3 text-sm text-[#334155]">
                  <div><span className="font-semibold">Chapter / Scope:</span> {previewRow.chapter || [...(previewRow.chapter_scope || []), ...(previewRow.topic_scope || [])].join(", ") || "—"}</div>
                  <div className="mt-2"><span className="font-semibold">Topic:</span> {previewRow.topic || [...(previewRow.topic_scope || [])].join(", ") || "—"}</div>
                </div>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-[#1D3469] mb-2">Competencies</h4>
                {previewCompetencyLoading ? (
                  <div className="rounded-2xl border border-[#e7eef9] bg-[#fafcff] px-4 py-3 text-sm text-[#64748b]">
                    Loading competency details…
                  </div>
                ) : Array.isArray(previewRow.competencies) && previewRow.competencies.length ? (
                  <div className="rounded-2xl border border-[#e7eef9] bg-[#fafcff] px-4 py-3">
                    <ul className="space-y-2 text-sm text-[#334155]">
                      {previewRow.competencies.map((item, idx) => {
                        const detail = previewCompetencyOptions.find((option) => option.id === item);
                        return (
                          <li key={`${item}-${idx}`} className="leading-6">
                            <span className="font-semibold text-[#1e5fb3]">{item}</span>
                            {detail?.text ? <span>{` - ${detail.text}`}</span> : null}
                            {detail?.goalId ? (
                              <span className="block text-xs text-[#64748b] mt-1">Goal: {detail.goalId}</span>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-[#e7eef9] bg-[#fafcff] px-4 py-3 text-sm text-[#64748b]">
                    No competencies stored for this row.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </ModuleCard>
  );
}
